import {MelodyEvent} from "./phrase-cycle";
import {MidiNote} from "./note-selection";

/** Downbeat vs other beats. */
type ClickSound = 'primary' | 'secondary';

/**
 * One onset in Phraser's merged event stream: the click track and the
 * melody, merged into a single sequential stream the unmodified kernel
 * Scheduler can consume. Merging the two parallel tilings into one timeline
 * before the scheduler means there is a single tempo-conversion point, so
 * the two-scheduler live-tempo divergence is impossible by construction.
 *
 * duration is the gap to the NEXT onset, not a note's length: a note
 * spanning several beats is split by the click onsets it crosses, and the
 * merge is what truncates durations at click onsets. The note's sounding
 * length travels separately, in the bundled note field (ADR 0003).
 *
 * The optional fields are honest: an onset may be click-only, note-only,
 * or both (a phrase always starts on the downbeat). An event with neither
 * field is a silent duration carrier - it arises when clicks are
 * silenced, and is required: the stream must tile time even when nothing
 * sounds.
 *
 * A rest needs no representation: with note-off (ADR 0003) the preceding
 * note goes silent exactly at its end, wherever beats fall, because
 * per-note oscillators schedule their own note-off at play time.
 */
/**
 * A note placed in time: its pitch and sounding length (in beats). Bundled,
 * not a flat pitch + length: a length without a pitch is meaningless, and
 * the bundle makes that guarantee structural (ADR 0003). length is the
 * note's sounding length, NOT the gap to the next onset - the merge
 * truncates the onset event's duration at click onsets, so the full length
 * travels alongside the pitch.
 */
interface NoteSpan {
    pitch: MidiNote;
    /** Sounding length, in beats (ADR 0003). */
    length: number;
}

interface PhraserEvent {
    /** Until the next onset, in beats. */
    duration: number;
    /** Present iff a click sounds at this onset. */
    click?: ClickSound;
    /** Present iff a note starts at this onset. */
    note?: NoteSpan;
}

/**
 * Onsets within this many beats are the same onset - cumulative float
 * durations can make a theoretically-integer note onset land at
 * 1.9999999999999998.
 */
const ONSET_EPSILON = 1e-9;

interface Onset {
    position: number;
    click?: ClickSound;
    note?: NoteSpan;
}

/**
 * Merge one bar's click track and melody into a single onset stream.
 *
 * Beat onsets (one per beat, primary on the downbeat) ALWAYS exist, even
 * when clicks are silenced - silencing drops the click field, never the
 * onset, so a silenced response bar still tiles the timeline. Note onsets
 * come from walking the melody batch and accumulating durations; rests
 * contribute duration but no onset (this handles both response bars and
 * future in-phrase rests - a rest is simply a span with no pitch onset).
 *
 * The result tiles the bar exactly: onsets strictly increasing in
 * [0, beatsPerBar), first at position 0, durations positive and summing to
 * beatsPerBar.
 */
function mergeBar(beatsPerBar: number, melody: MelodyEvent[]): PhraserEvent[] {
    const onsets: Onset[] = [];
    for (let beat = 0; beat < beatsPerBar; beat++) {
        onsets.push({position: beat, click: beat === 0 ? 'primary' : 'secondary'});
    }

    let position = 0;
    for (const event of melody) {
        if (event.kind === 'note') {
            // carry the whole duration as the note's length: the merge
            // truncates the onset event's duration at click onsets, so the
            // full sounding length must travel alongside the pitch
            addNoteOnset(onsets, position, {pitch: event.pitch, length: event.duration});
        }
        position += event.duration;
    }

    return onsets.map((onset, i) => {
        const nextPosition = i + 1 < onsets.length ? onsets[i + 1].position : beatsPerBar;
        return {
            duration: nextPosition - onset.position,
            ...(onset.click !== undefined && {click: onset.click}),
            ...(onset.note !== undefined && {note: onset.note}),
        };
    });
}

/** Record a note onset, merging into an existing onset at (within epsilon of) the position. */
function addNoteOnset(onsets: Onset[], position: number, note: NoteSpan) {
    const existing = onsets.find(o => Math.abs(o.position - position) < ONSET_EPSILON);
    if (existing) {
        existing.note = note;
        return;
    }
    const index = onsets.findIndex(o => o.position > position);
    onsets.splice(index === -1 ? onsets.length : index, 0, {position, note});
}

/**
 * The count-in seed: countInBars bars of click-only PhraserEvents, one beat
 * each, primary on each bar's first beat. Count-in bars are exactly normal
 * click bars; passed to scheduler.start(seed), so no count-in logic exists
 * anywhere else. When clicks are silenced the seed stays silent-but-present
 * (durations without click fields), so phrase placement is unchanged.
 */
function countInSeed(countInBars: number, beatsPerBar: number, hasClickTrack: boolean): PhraserEvent[] {
    const seed: PhraserEvent[] = [];
    for (let bar = 0; bar < countInBars; bar++) {
        for (let beat = 0; beat < beatsPerBar; beat++) {
            seed.push({
                duration: 1,
                ...(hasClickTrack && {click: beat === 0 ? 'primary' as const : 'secondary' as const}),
            });
        }
    }
    return seed;
}

export {ClickSound, NoteSpan, PhraserEvent, mergeBar, countInSeed, ONSET_EPSILON};
